import React from 'react';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import { GatsbyImage } from 'gatsby-plugin-image';

import AssessmentModel from '../models/Assessment/Assessment';

import JsonLd from '../components/jsonld.jsx';
import Layout from '../components/layout';
import { Paper, TitleSet, AnimateIn, InternalLink, Container, media } from '../ui-components';
import SEO from '../components/seo';
import Video from '../components/video';
import SectionLayout from '../components/sections/section-layout';

import renderRichText from '../utils/render-rich-text';

/**
 * Component that renders an Assessment's content
 *
 * @component Assessment
 *
 * @param {object} props
 *
 * @prop {object} data
 * @prop {object} pageContext
 *
 * @returns {JSX}
 */
const Assessment = props => {
  const { data, pageContext } = props;
  const assessmentModel = new AssessmentModel(data.contentfulAssessment);

  /**
   * Renders the SEO metatag and structured data elements
   *
   * @function renderSEO
   *
   * @returns {JSX}
   */
  const renderSEO = () => {
    return (
      <>
        <SEO
          title={assessmentModel.assessmentName}
          description={assessmentModel.headline}
          slug={`assessment/${assessmentModel.slug}`}
          image={assessmentModel.heroImage?.images?.fallback?.src}
        />
        <JsonLd
          item={{
            '@context': 'https://schema.org',
            '@type': 'Article',
            name: assessmentModel.assessmentName,
            image: assessmentModel.heroImage?.images?.fallback?.src
          }}
        />
      </>
    );
  };


  return (
    <Layout>
      {renderSEO()}
      <Paper bottom>
        <AnimateIn onScroll>
          <TitleSection sectionContainer>
            <TitleSet
              isLandingPageWidth
              headingLevel={1}
              label={assessmentModel.subHeadline}
              title={assessmentModel.headline}
              bodyText={assessmentModel.richDescription ? renderRichText(assessmentModel.richDescription) : ''}
              underlinedText={assessmentModel.headlineUnderline}
            />
            {assessmentModel.contactButton && (
              <InternalLink linkstyle="primary-button" to="/contact">
                {assessmentModel.contactButtonText}
              </InternalLink>
            )}
          </TitleSection>
        </AnimateIn>
      </Paper>
      {assessmentModel?.heroImage && (
        <AssessmentHeroContainer condensed className="insight-body">
          <ImgMain
            loading="eager"
            fetchpriority="high"
            image={assessmentModel.heroImage}
            alt={assessmentModel.heroImage.title}
          />
        </AssessmentHeroContainer>
      )}
      {assessmentModel.sectionTemplates && (
        <SectionLayout
          sections={assessmentModel.sectionTemplates}
          geoSpace={pageContext.space}
          slug={assessmentModel.slug}
        />
      )}
    </Layout>
  );
};

// This is needed for the optional contact button
const TitleSection = styled(props => <Container {...props} />)`
  padding-top: 120px;

  @media screen and (max-width: ${media.mediumTop}) {
    padding-top: 60px;
  }
`;

const ImgMain = styled(props => <GatsbyImage {...props} />)`
  box-shadow: 2px 2px 14px -1px rgba(58, 58, 58, 0.75);
  margin-top: -180px;

  @media screen and (max-width: ${media.mediumTop}) {
    margin-top: -110px;
  }
`;

const AssessmentHeroContainer = styled(props => <Container {...props} />)`
  padding-bottom: 60px;

  @media screen and (max-width: ${media.mediumTop}) {
    padding-bottom: 0px;
  }
`;

export default Assessment;

export const pageQuery = graphql`
  query AssessmentBySlug($slug: String!, $spaceId: String) {
    contentfulAssessment(slug: { eq: $slug }, spaceId: { eq: $spaceId }) {
      ...CompleteAssessment
    }
  }
`;
